// ══════════════════════════════════════════════════════════════
// COMPASS – Process Reducer
// ══════════════════════════════════════════════════════════════
import { createReducer, on } from '@ngrx/store';
import { ProcessActions } from './process.actions';
import {
  initialProcessState,
  ProcessState,
  ProcessObservationState,
  initialObservationState,
  initialComplianceReportState,
} from './process.state';
import { KycProcessDto } from '../../api/models/kyc-process-dto';
import { ProcessObservationKind } from '../../models/process-observation.model';

/** One kind of observation changed, the others kept as they are. */
function withObservation(
  state: ProcessState,
  kind: ProcessObservationKind,
  change: (current: ProcessObservationState) => Partial<ProcessObservationState>,
): ProcessState {
  const current = state.observations[kind];
  return { ...state, observations: { ...state.observations, [kind]: { ...current, ...change(current) } } };
}

/** Another process opened: every observation is forgotten, but the revisions keep counting (a card compares them). */
function resetObservations(
  observations: Record<ProcessObservationKind, ProcessObservationState>,
): Record<ProcessObservationKind, ProcessObservationState> {
  const reset = {} as Record<ProcessObservationKind, ProcessObservationState>;
  for (const kind of Object.keys(observations) as ProcessObservationKind[]) {
    reset[kind] = { ...initialObservationState, revision: observations[kind].revision };
  }
  return reset;
}

/** US-003: only GET /kyc/processes/{id} fills a process's operation, null in the responses of a status change or a
 *  reopen, which do not change it: the one already loaded for the same process stays. */
function keepOperation(process: KycProcessDto, current: KycProcessDto | null): KycProcessDto {
  const operation = current?.id === process.id ? current?.operation : undefined;
  return process.operation || !operation ? process : { ...process, operation };
}

export const processReducer = createReducer<ProcessState>(
  initialProcessState,

  // Load Processes
  on(ProcessActions.loadProcesses, (state): ProcessState => ({
    ...state,
    loading: true,
    processesLoading: true,
    error: null,
  })),

  on(ProcessActions.loadProcessesSuccess, (state, { processes }): ProcessState => ({
    ...state,
    processes,
    loading: false,
    processesLoading: false,
    error: null,
  })),

  on(ProcessActions.loadProcessesFailure, (state, { error }): ProcessState => ({
    ...state,
    loading: false,
    processesLoading: false,
    error,
  })),

  // Select Process (from already-loaded list, clears forms, change requests, approvals, timeline, participants, risk matrix, screenings)
  on(ProcessActions.selectProcess, (state, { processId }): ProcessState => {
    if (state.selectedProcess?.id === processId) return state;
    return {
      ...state,
      // The list endpoint now returns a lightweight DTO that doesn't satisfy
      // KycProcessDto. loadProcess fires immediately after selectProcess and
      // populates selectedProcess with the full DTO — null here is fine.
      selectedProcess: null,
      individualForm: null,
      companyForm: null,
      changeRequests: [],
      approvalState: null,
      approvals: [],
      approvalsLoading: false,
      approvalsError: null,
      timeline: [],
      participants: [],
      riskMatrixState: null,
      assessResult: null,
      riskMeasures: null,
      observations: resetObservations(state.observations),
      screenings: [],
      screeningsLoading: false,
      screeningsLoaded: false,
      screeningsError: null,
      screeningsRunning: false,
      worldcheckCases: [],
      worldcheckCasesLoading: false,
      worldcheckCasesEmpty: false,
      worldcheckCasesError: null,
      worldcheckFinalStatus: null,
      worldcheckMissingTargets: [],
      kycReport: null,
      kycReportLoading: false,
      complianceReport: initialComplianceReportState,
      signatureStatus: null,
      signatureStatusLoading: false,
      signatureStatusLoaded: false,
      signatureStatusFailed: false,
      signatureRequesting: false,
      signatureSkipping: false,
      evidenceFolders: [],
      evidenceFoldersLoading: false,
      consentEvidence: null,
      consentEvidenceLoaded: false,
      consentEvidenceHidden: false,
      consentEvidenceLoadFailed: false,
      consentEvidenceUploading: false,
    };
  }),

  // Load single Process by ID (deep link)
  on(ProcessActions.loadProcess, (state): ProcessState => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(ProcessActions.loadProcessSuccess, (state, { process }): ProcessState => ({
    ...state,
    selectedProcess: process,
    individualForm: null,
    companyForm: null,
    changeRequests: [],
    approvalState: null,
    approvals: [],
    approvalsLoading: false,
    approvalsError: null,
    timeline: [],
    participants: [],
    riskMatrixState: null,
    assessResult: null,
    riskMeasures: null,
    // The open process reloaded keeps them: its cards loaded them already and will not ask again.
    observations: state.selectedProcess?.id === process.id
      ? state.observations
      : resetObservations(state.observations),
    screenings: [],
    screeningsLoading: false,
    screeningsRunning: false,
    worldcheckCases: [],
    worldcheckCasesLoading: false,
    worldcheckCasesEmpty: false,
    worldcheckCasesError: null,
    kycReport: null,
    kycReportLoading: false,
    // Like the observations: the open process reloaded keeps it (its section asked once, and polls on its own).
    complianceReport: state.selectedProcess?.id === process.id
      ? state.complianceReport
      : initialComplianceReportState,
    signatureStatus: null,
    signatureStatusLoading: false,
    signatureStatusLoaded: false,
    signatureStatusFailed: false,
    signatureRequesting: false,
    signatureSkipping: false,
    evidenceFolders: [],
    evidenceFoldersLoading: false,
    consentEvidence: null,
    consentEvidenceLoaded: false,
    consentEvidenceHidden: false,
    consentEvidenceLoadFailed: false,
    consentEvidenceUploading: false,
    loading: false,
    error: null,
  })),

  on(ProcessActions.loadProcessFailure, (state, { error }): ProcessState => ({
    ...state,
    loading: false,
    error,
  })),

  // US-025: a deleted process leaves the list and the page at once (the dashboard reloads the list on arrival).
  on(ProcessActions.deleteProcessSuccess, (state, { processId }): ProcessState => ({
    ...state,
    processes: state.processes.filter((row) => row.processId !== processId),
    selectedProcess: state.selectedProcess?.id === processId ? null : state.selectedProcess,
  })),

  // A 404: someone else deleted it first, so its cached row goes too.
  on(ProcessActions.deleteProcessFailure, (state, { processId, status }): ProcessState => status === 404
    ? { ...state, processes: state.processes.filter((row) => row.processId !== processId) }
    : state),

  // A Pending row withdrawn (or already withdrawn elsewhere: 404): its row leaves the list. Only the Pending row: once
  // accepted, the customer's process row stays (the backend refuses that with 409).
  on(ProcessActions.withdrawInvitationSuccess, (state, { customerId }): ProcessState => ({
    ...state,
    processes: state.processes.filter((row) => row.processId || row.customerId !== customerId),
  })),

  on(ProcessActions.withdrawInvitationFailure, (state, { customerId, status }): ProcessState => status === 404
    ? { ...state, processes: state.processes.filter((row) => row.processId || row.customerId !== customerId) }
    : state),

  // Background status refresh — only updates the process itself; preserves
  // changeRequests, timeline, forms, risk matrix, screenings, etc.
  on(ProcessActions.refreshProcessStatusSuccess, (state, { process }): ProcessState => ({
    ...state,
    selectedProcess: process,
  })),

  // Load Individual Form
  on(ProcessActions.loadIndividualForm, (state): ProcessState => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(ProcessActions.loadIndividualFormSuccess, (state, { form }): ProcessState => ({
    ...state,
    individualForm: form,
    loading: false,
    error: null,
  })),

  on(ProcessActions.loadIndividualFormFailure, (state, { error }): ProcessState => ({
    ...state,
    loading: false,
    error,
  })),

  // Save Individual Form
  on(ProcessActions.saveIndividualForm, (state): ProcessState => ({
    ...state,
    formSaving: true,
    error: null,
  })),

  on(ProcessActions.saveIndividualFormSuccess, (state, { form }): ProcessState => ({
    ...state,
    individualForm: form,
    formSaving: false,
    error: null,
  })),

  on(ProcessActions.saveIndividualFormFailure, (state, { error }): ProcessState => ({
    ...state,
    formSaving: false,
    error,
  })),

  // Load Company Form
  on(ProcessActions.loadCompanyForm, (state): ProcessState => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(ProcessActions.loadCompanyFormSuccess, (state, { form }): ProcessState => ({
    ...state,
    companyForm: form,
    loading: false,
    error: null,
  })),

  on(ProcessActions.loadCompanyFormFailure, (state, { error }): ProcessState => ({
    ...state,
    loading: false,
    error,
  })),

  // Save Company Form
  on(ProcessActions.saveCompanyForm, (state): ProcessState => ({
    ...state,
    formSaving: true,
    error: null,
  })),

  on(ProcessActions.saveCompanyFormSuccess, (state, { form }): ProcessState => ({
    ...state,
    companyForm: form,
    formSaving: false,
    error: null,
  })),

  on(ProcessActions.saveCompanyFormFailure, (state, { error }): ProcessState => ({
    ...state,
    formSaving: false,
    error,
  })),

  // Change Requests
  on(ProcessActions.loadChangeRequests, (state): ProcessState => ({
    ...state,
    changeRequestsLoading: true,
  })),

  on(ProcessActions.loadChangeRequestsSuccess, (state, { changeRequests }): ProcessState => ({
    ...state,
    changeRequests,
    changeRequestsLoading: false,
  })),

  on(ProcessActions.loadChangeRequestsFailure, (state, { error }): ProcessState => ({
    ...state,
    changeRequestsLoading: false,
    error,
  })),

  on(ProcessActions.createChangeRequestSuccess, (state, { changeRequest }): ProcessState => ({
    ...state,
    changeRequests: [...state.changeRequests, changeRequest],
  })),

  on(ProcessActions.closeChangeRequestSuccess, (state, { changeRequest }): ProcessState => ({
    ...state,
    changeRequests: state.changeRequests.map((cr) =>
      cr.id === changeRequest.id ? { ...cr, status: changeRequest.status } : cr
    ),
  })),

  on(ProcessActions.reopenChangeRequestSuccess, (state, { changeRequest }): ProcessState => ({
    ...state,
    changeRequests: state.changeRequests.map((cr) =>
      cr.id === changeRequest.id ? { ...cr, status: changeRequest.status } : cr
    ),
  })),

  // Status Transition & Submit
  on(ProcessActions.transitionStatus, ProcessActions.submitProcess, (state): ProcessState => ({
    ...state,
    statusTransitioning: true,
    error: null,
  })),

  on(ProcessActions.transitionStatusSuccess, ProcessActions.submitProcessSuccess, (state, { process }): ProcessState => ({
    ...state,
    statusTransitioning: false,
    selectedProcess: keepOperation(process, state.selectedProcess),
    // Patch the matching list row in place. The list DTO is a strict subset of
    // KycProcessDto with renamed keys (processId/clientName/riskLevel/revalidationDate)
    // — keep the row's existing fields and overlay the freshly mutated ones.
    processes: state.processes.map((p) =>
      p.processId === process.id
        ? {
            ...p,
            status: process.status,
            riskLevel: process.riskLevelCurrent,
            updatedAt: process.updatedAt,
            revalidationDate: process.refreshDueAt ?? p.revalidationDate,
          }
        : p
    ),
    error: null,
  })),

  on(ProcessActions.transitionStatusFailure, ProcessActions.submitProcessFailure, (state, { error }): ProcessState => ({
    ...state,
    statusTransitioning: false,
    error,
  })),

  // Compliance Officer reopen
  on(ProcessActions.reopenProcess, (state): ProcessState => ({
    ...state,
    reopening: true,
    error: null,
  })),

  on(ProcessActions.reopenProcessSuccess, ProcessActions.clientReopenProcessSuccess, (state, { process }): ProcessState => ({
    ...state,
    reopening: false,
    clientReopening: false,
    // The reopen response leaves submissionCount out, and a reopen doesn't change it.
    selectedProcess: {
      ...keepOperation(process, state.selectedProcess),
      submissionCount: process.submissionCount ?? state.selectedProcess?.submissionCount,
    },
    processes: state.processes.map((p) =>
      p.processId === process.id
        ? {
            ...p,
            status: process.status,
            riskLevel: process.riskLevelCurrent,
            updatedAt: process.updatedAt,
            revalidationDate: process.refreshDueAt ?? p.revalidationDate,
          }
        : p
    ),
    error: null,
  })),

  on(ProcessActions.reopenProcessFailure, (state, { error }): ProcessState => ({
    ...state,
    reopening: false,
    error,
  })),

  on(ProcessActions.clientReopenProcess, (state): ProcessState => ({
    ...state,
    clientReopening: true,
    error: null,
  })),

  on(ProcessActions.clientReopenProcessFailure, (state, { error }): ProcessState => ({
    ...state,
    clientReopening: false,
    error,
  })),

  // Form version history
  on(ProcessActions.loadFormVersions, (state): ProcessState => ({
    ...state,
    formVersionsLoading: true,
  })),

  on(ProcessActions.loadFormVersionsSuccess, (state, { versions }): ProcessState => ({
    ...state,
    formVersions: versions,
    formVersionsLoading: false,
  })),

  on(ProcessActions.loadFormVersionsFailure, (state, { error }): ProcessState => ({
    ...state,
    formVersionsLoading: false,
    error,
  })),

  on(ProcessActions.loadFormVersionDetail, (state): ProcessState => ({
    ...state,
    formVersionDetailLoading: true,
  })),

  on(ProcessActions.loadFormVersionDetailSuccess, (state, { version }): ProcessState => ({
    ...state,
    selectedFormVersion: version,
    formVersionDetailLoading: false,
  })),

  on(ProcessActions.loadFormVersionDetailFailure, (state, { error }): ProcessState => ({
    ...state,
    formVersionDetailLoading: false,
    error,
  })),

  on(ProcessActions.clearFormVersionDetail, (state): ProcessState => ({
    ...state,
    selectedFormVersion: null,
  })),

  // Approval Workflow
  on(ProcessActions.loadApprovalState, (state): ProcessState => ({
    ...state,
    approvalLoading: true,
  })),

  on(ProcessActions.loadApprovalStateSuccess, (state, { approvalState }): ProcessState => ({
    ...state,
    approvalState,
    approvalLoading: false,
  })),

  on(ProcessActions.loadApprovalStateFailure, (state): ProcessState => ({
    ...state,
    approvalLoading: false,
  })),

  on(ProcessActions.loadApprovals, (state): ProcessState => ({
    ...state,
    approvalLoading: true,
    approvalsLoading: true,
    approvalsError: null,
  })),

  on(ProcessActions.loadApprovalsSuccess, (state, { approvals }): ProcessState => ({
    ...state,
    approvals,
    approvalLoading: false,
    approvalsLoading: false,
  })),

  on(ProcessActions.loadApprovalsFailure, (state, { error }): ProcessState => ({
    ...state,
    approvalLoading: false,
    approvalsLoading: false,
    approvalsError: error,
  })),

  on(ProcessActions.decideApproval, (state): ProcessState => ({
    ...state,
    approvalDeciding: true,
    error: null,
  })),

  on(ProcessActions.decideApprovalSuccess, (state, { approval }): ProcessState => ({
    ...state,
    approvals: [...state.approvals, approval],
    approvalDeciding: false,
    error: null,
  })),

  on(ProcessActions.decideApprovalFailure, (state, { error }): ProcessState => ({
    ...state,
    approvalDeciding: false,
    error,
  })),

  // Timeline
  on(ProcessActions.loadTimeline, (state): ProcessState => ({
    ...state,
    timelineLoading: true,
  })),

  on(ProcessActions.loadTimelineSuccess, (state, { events }): ProcessState => ({
    ...state,
    timeline: events,
    timelineLoading: false,
  })),

  on(ProcessActions.loadTimelineFailure, (state): ProcessState => ({
    ...state,
    timelineLoading: false,
  })),

  // Participants
  on(ProcessActions.loadParticipants, (state): ProcessState => ({
    ...state,
    participantsLoading: true,
  })),

  on(ProcessActions.loadParticipantsSuccess, (state, { participants }): ProcessState => ({
    ...state,
    participants,
    participantsLoading: false,
  })),

  on(ProcessActions.loadParticipantsFailure, (state): ProcessState => ({
    ...state,
    participantsLoading: false,
  })),

  // Risk Matrix
  on(ProcessActions.loadRiskMatrixState, (state): ProcessState => ({
    ...state,
    riskMatrixLoading: true,
  })),

  on(ProcessActions.loadRiskMatrixStateSuccess, (state, { riskMatrixState }): ProcessState => ({
    ...state,
    riskMatrixState,
    riskMatrixLoading: false,
  })),

  on(ProcessActions.loadRiskMatrixStateFailure, (state): ProcessState => ({
    ...state,
    riskMatrixLoading: false,
  })),

  on(ProcessActions.assessRiskMatrix, (state): ProcessState => ({
    ...state,
    riskMatrixAssessing: true,
    error: null,
  })),

  on(ProcessActions.assessRiskMatrixSuccess, (state, { response }): ProcessState => ({
    ...state,
    riskMatrixAssessing: false,
    assessResult: response,
    error: null,
  })),

  on(ProcessActions.assessRiskMatrixFailure, (state, { error }): ProcessState => ({
    ...state,
    riskMatrixAssessing: false,
    error,
  })),

  // Risk Measures
  on(ProcessActions.loadRiskMeasures, (state): ProcessState => ({
    ...state,
    riskMeasuresLoading: true,
  })),

  on(ProcessActions.loadRiskMeasuresSuccess, (state, { riskMeasures }): ProcessState => ({
    ...state,
    riskMeasures,
    riskMeasuresLoading: false,
  })),

  on(ProcessActions.loadRiskMeasuresFailure, (state): ProcessState => ({
    ...state,
    riskMeasuresLoading: false,
  })),

  on(ProcessActions.saveRiskMeasures, (state): ProcessState => ({
    ...state,
    riskMeasuresSaving: true,
    error: null,
  })),

  // Risk assessment results set by hand (US-011)
  on(ProcessActions.saveRiskResults, (state): ProcessState => ({
    ...state,
    riskResultsSaving: true,
  })),

  // The stored results replace the panel's (an answer for a process no longer open only ends the save).
  on(ProcessActions.saveRiskResultsSuccess, (state, { processId, results }): ProcessState =>
    // An answer for a process no longer open, or one without results (the state is read again), only ends the save.
    state.selectedProcess?.id !== processId || Object.keys(results).length === 0
      ? { ...state, riskResultsSaving: false } : {
      ...state,
      riskResultsSaving: false,
      riskMatrixState: state.riskMatrixState && {
        ...state.riskMatrixState,
        globalScore: results.globalScore,
        riskLevel: results.riskLevel,
        riskProfile: results.riskProfile,
        decision: results.decision,
        measures: results.measures,
        reassessmentDate: results.reassessmentDate,
        resultsEditedByHand: results.resultsEditedByHand,
        calculatedGlobalScore: results.calculatedGlobalScore,
      },
    }),

  on(ProcessActions.saveRiskResultsFailure, (state): ProcessState => ({
    ...state,
    riskResultsSaving: false,
  })),

  on(ProcessActions.saveRiskMeasuresSuccess, (state, { riskMeasures }): ProcessState => ({
    ...state,
    riskMeasures,
    riskMeasuresSaving: false,
  })),

  on(ProcessActions.saveRiskMeasuresFailure, (state, { error }): ProcessState => ({
    ...state,
    riskMeasuresSaving: false,
    error,
  })),

  // The Compliance Officer's observations (US-005, US-009), each kind on its own
  on(ProcessActions.loadObservation, (state, { kind }): ProcessState =>
    withObservation(state, kind, () => ({ loading: true, loadFailed: false }))),

  // An answer for a process no longer open (the officer moved on before it came back) is dropped.
  on(ProcessActions.loadObservationSuccess, (state, { processId, kind, text }): ProcessState =>
    state.selectedProcess?.id !== processId ? state : withObservation(state, kind, (current) => ({
      text,
      loading: false,
      loaded: true,
      revision: current.revision + 1,
    }))),

  // Not loaded: the field stays closed, so a save cannot overwrite a text it never showed.
  on(ProcessActions.loadObservationFailure, (state, { processId, kind }): ProcessState =>
    state.selectedProcess?.id !== processId ? state : withObservation(state, kind, () => ({
      loading: false,
      loadFailed: true,
    }))),

  on(ProcessActions.saveObservation, (state, { kind }): ProcessState =>
    withObservation(state, kind, () => ({ saving: true }))),

  // The backend's stored value: blank becomes null and characters the report cannot print are dropped.
  on(ProcessActions.saveObservationSuccess, (state, { processId, kind, text }): ProcessState =>
    state.selectedProcess?.id !== processId ? state : withObservation(state, kind, (current) => ({
      text,
      saving: false,
      revision: current.revision + 1,
    }))),

  on(ProcessActions.saveObservationFailure, (state, { processId, kind }): ProcessState =>
    state.selectedProcess?.id !== processId ? state : withObservation(state, kind, () => ({ saving: false }))),

  // Screenings
  on(ProcessActions.loadScreenings, (state): ProcessState => ({
    ...state,
    screeningsLoading: true,
    screeningsError: null,
  })),

  on(ProcessActions.loadScreeningsSuccess, (state, { screenings }): ProcessState => ({
    ...state,
    screenings,
    screeningsLoading: false,
    screeningsLoaded: true,
    screeningsError: null,
  })),

  on(ProcessActions.loadScreeningsFailure, (state, { error }): ProcessState => ({
    ...state,
    screeningsLoading: false,
    screeningsLoaded: true,
    screeningsError: error,
  })),

  on(ProcessActions.runScreenings, (state): ProcessState => ({
    ...state,
    screeningsRunning: true,
  })),

  // A run on an already screened process returns only the rows it newly saved, not the full list,
  // so it doesn't touch the screenings: runScreenings$ reloads them (and the World-Check status).
  on(ProcessActions.runScreeningsSuccess, (state): ProcessState => ({
    ...state,
    screeningsRunning: false,
  })),

  on(ProcessActions.runScreeningsFailure, (state): ProcessState => ({
    ...state,
    screeningsRunning: false,
  })),

  // World Check One cases
  on(ProcessActions.loadWorldcheckCases, (state): ProcessState => ({
    ...state,
    worldcheckCasesLoading: true,
    worldcheckCasesError: null,
    worldcheckCasesEmpty: false,
  })),

  on(ProcessActions.loadWorldcheckCasesSuccess, (state, { cases, finalStatus, missingTargets }): ProcessState => ({
    ...state,
    worldcheckCases: cases,
    worldcheckCasesLoading: false,
    worldcheckCasesEmpty: false,
    worldcheckCasesError: null,
    worldcheckFinalStatus: finalStatus,
    worldcheckMissingTargets: missingTargets,
  })),

  on(ProcessActions.loadWorldcheckCasesEmpty, (state, { finalStatus, missingTargets }): ProcessState => ({
    ...state,
    worldcheckCases: [],
    worldcheckCasesLoading: false,
    worldcheckCasesEmpty: true,
    worldcheckCasesError: null,
    worldcheckFinalStatus: finalStatus,
    worldcheckMissingTargets: missingTargets,
  })),

  on(ProcessActions.loadWorldcheckCasesFailure, (state, { error }): ProcessState => ({
    ...state,
    worldcheckCasesLoading: false,
    worldcheckCasesError: error,
  })),

  // Statistics
  on(ProcessActions.loadProcessStatistics, (state): ProcessState => ({
    ...state,
    statisticsLoading: true,
  })),

  on(ProcessActions.loadProcessStatisticsSuccess, (state, { statistics }): ProcessState => ({
    ...state,
    statistics,
    statisticsLoading: false,
  })),

  on(ProcessActions.loadProcessStatisticsFailure, (state): ProcessState => ({
    ...state,
    statisticsLoading: false,
  })),

  // KYC Final Report PDF
  on(ProcessActions.loadKycReport, (state): ProcessState => ({
    ...state,
    kycReportLoading: true,
  })),

  on(ProcessActions.loadKycReportSuccess, (state, { report }): ProcessState => ({
    ...state,
    kycReport: report,
    kycReportLoading: false,
  })),

  on(ProcessActions.loadKycReportEmpty, (state): ProcessState => ({
    ...state,
    kycReport: null,
    kycReportLoading: false,
    signatureStatus: null,
    signatureStatusLoading: false,
  })),

  on(ProcessActions.loadKycReportFailure, (state): ProcessState => ({
    ...state,
    kycReportLoading: false,
  })),

  // Full compliance report (US-003): answers for another process (the page moved on) are dropped.
  // A spinner (with nothing shown yet) for a load the user asked for; a background one changes nothing until it answers.
  on(ProcessActions.loadComplianceReport, (state, { poll, silent }): ProcessState => poll || silent ? state : ({
    ...state,
    complianceReport: { ...state.complianceReport, loading: true, error: null, waitEnded: false },
  })),

  on(ProcessActions.loadComplianceReportSuccess, (state, { processId, report }): ProcessState =>
    state.selectedProcess?.id !== processId ? state : ({
      ...state,
      complianceReport: {
        ...state.complianceReport,
        report,
        loading: false,
        loaded: true,
        error: null,
        waitEnded: false,
        // Still the one from before the change: the update is still on its way.
        updating: state.complianceReport.updating && report.generatedAt === state.complianceReport.updatingFrom,
      },
    })),

  on(ProcessActions.loadComplianceReportEmpty, (state, { processId }): ProcessState =>
    state.selectedProcess?.id !== processId ? state : ({
      ...state,
      complianceReport: { ...state.complianceReport, report: null, loading: false, loaded: true, error: null, updating: false },
    })),

  // A background load's failure (silent) ends a load it replaced, and otherwise leaves the section as it is.
  on(ProcessActions.loadComplianceReportFailure, (state, { processId, error, silent }): ProcessState =>
    state.selectedProcess?.id !== processId ? state : ({
      ...state,
      complianceReport: { ...state.complianceReport, loading: false, error: silent ? state.complianceReport.error : error },
    })),

  on(ProcessActions.complianceReportOutdated, (state, { processId }): ProcessState =>
    state.selectedProcess?.id !== processId ? state : ({
      ...state,
      complianceReport: {
        ...state.complianceReport,
        updating: true,
        waitEnded: false,
        // A second change while waiting keeps the first one's baseline.
        updatingFrom: state.complianceReport.updating
          ? state.complianceReport.updatingFrom
          : state.complianceReport.report?.generatedAt ?? null,
      },
    })),

  // Not said to be on its way any more: the report shown stays, or "not ready yet" with a retry.
  on(ProcessActions.complianceReportWaitEnded, (state, { processId }): ProcessState =>
    state.selectedProcess?.id !== processId ? state : ({
      ...state,
      complianceReport: { ...state.complianceReport, updating: false, waitEnded: true },
    })),

  // Entrust signature
  on(ProcessActions.loadSignatureStatus, (state): ProcessState => ({
    ...state,
    signatureStatusLoading: true,
  })),

  on(ProcessActions.loadSignatureStatusSuccess, (state, { status }): ProcessState => ({
    ...state,
    signatureStatus: status,
    signatureStatusLoading: false,
    signatureStatusLoaded: true,
    signatureStatusFailed: false,
  })),

  on(ProcessActions.loadSignatureStatusEmpty, (state): ProcessState => ({
    ...state,
    signatureStatus: null,
    signatureStatusLoading: false,
    signatureStatusLoaded: true,
    signatureStatusFailed: false,
  })),

  // Answered even on failure, so the client's Edit isn't hidden for good by one failed call: the backend
  // refuses a reopen once a signature exists (client-reopen-signature-started).
  on(ProcessActions.loadSignatureStatusFailure, (state): ProcessState => ({
    ...state,
    signatureStatusLoading: false,
    signatureStatusLoaded: true,
    signatureStatusFailed: true,
  })),

  on(ProcessActions.requestSignature, (state): ProcessState => ({
    ...state,
    signatureRequesting: true,
  })),

  // Until the status reload lands: the started run is the client's, or in CLIENT_AND_BOARD_MEMBER mode the
  // board member's, who signs first (US-042) - never shown as the client's.
  on(ProcessActions.requestSignatureSuccess, (state, { response, mode }): ProcessState => ({
    ...state,
    signatureStatus: mode === 'CLIENT_AND_BOARD_MEMBER'
      ? {
        kycProcessId: response.kycProcessId,
        mode,
        currentSigner: 'BOARD_MEMBER',
        boardMember: { workflowRunId: response.workflowRunId, status: response.status },
      }
      : {
        kycProcessId: response.kycProcessId,
        mode,
        currentSigner: 'CLIENT',
        workflowRunId: response.workflowRunId,
        status: response.status,
      },
    signatureRequesting: false,
  })),

  on(ProcessActions.requestSignatureFailure, (state): ProcessState => ({
    ...state,
    signatureRequesting: false,
  })),

  on(ProcessActions.skipSignature, (state): ProcessState => ({
    ...state,
    signatureSkipping: true,
  })),

  on(ProcessActions.skipSignatureSuccess, (state, { status }): ProcessState => ({
    ...state,
    signatureStatus: status,
    signatureSkipping: false,
  })),

  on(ProcessActions.skipSignatureFailure, (state): ProcessState => ({
    ...state,
    signatureSkipping: false,
  })),

  // Entrust signature evidence folders
  on(ProcessActions.loadEvidenceFolders, (state): ProcessState => ({
    ...state,
    evidenceFoldersLoading: true,
  })),

  // Client consent evidence (US-015); an answer for a process no longer open is dropped.
  on(ProcessActions.loadConsentEvidence, (state): ProcessState => ({ ...state, consentEvidenceLoadFailed: false })),
  on(ProcessActions.loadConsentEvidenceSuccess, (state, { processId, evidence }): ProcessState =>
    state.selectedProcess?.id !== processId ? state : {
      ...state,
      consentEvidence: evidence,
      consentEvidenceLoaded: true,
      consentEvidenceHidden: false,
      consentEvidenceLoadFailed: false,
    }),
  on(ProcessActions.loadConsentEvidenceHidden, (state, { processId }): ProcessState =>
    state.selectedProcess?.id !== processId ? state : {
      ...state,
      consentEvidence: null,
      consentEvidenceLoaded: true,
      consentEvidenceHidden: true,
      consentEvidenceLoadFailed: false,
    }),
  on(ProcessActions.loadConsentEvidenceFailure, (state, { processId }): ProcessState =>
    state.selectedProcess?.id !== processId ? state : { ...state, consentEvidenceLoadFailed: true }),
  on(ProcessActions.uploadConsentEvidence, (state): ProcessState => ({ ...state, consentEvidenceUploading: true })),
  // The new document is the evidence only once stored: a failed replacement keeps the current one.
  on(ProcessActions.uploadConsentEvidenceSuccess, (state, { processId, evidence }): ProcessState =>
    state.selectedProcess?.id !== processId ? state : {
      ...state,
      consentEvidence: evidence,
      consentEvidenceLoaded: true,
      consentEvidenceLoadFailed: false,
      consentEvidenceUploading: false,
    }),
  on(ProcessActions.uploadConsentEvidenceFailure, (state, { processId }): ProcessState =>
    state.selectedProcess?.id !== processId ? state : { ...state, consentEvidenceUploading: false }),

  on(ProcessActions.loadEvidenceFoldersSuccess, (state, { evidenceFolders }): ProcessState => ({
    ...state,
    evidenceFolders,
    evidenceFoldersLoading: false,
  })),

  on(ProcessActions.loadEvidenceFoldersFailure, (state): ProcessState => ({
    ...state,
    evidenceFoldersLoading: false,
  })),

  // Clear
  on(ProcessActions.clearProcesses, (): ProcessState => ({
    ...initialProcessState,
  })),
);
